import i18next from "i18next";

import { CourseTimespanModelCollection } from "./CourseTimespanModelCollection";
import { get, has, set, merge, last, isNil, uniq } from "@/utils/object";

import type { SchedulingTimeRangeModelCollection } from "../SchedulingTimeRange/SchedulingTimeRangeModelCollection";
import { getSlotHandler } from "./SlotValidator";
import { SchedulingTimeRangeModel } from "../SchedulingTimeRange/SchedulingTimeRangeModel";
import type { CourseTimespansQuantityStepModelCollection } from "../CourseTimespansQuantitySteps/CourseTimespansQuantityStepModelCollection";

import type { BookingSlotInterface } from "../../FormManager/types";
import { BookModel } from "../BookModel/BookModel";
import { BeeModelCollection } from "../../beeorm/BeeModelCollection";
import { UserModel } from "../User/UserModel";
import { AppointmentModel } from "../Appointments/AppointmentModel";
import {
  parseISO,
  addMinutes,
  setMilliseconds,
  setDate as setDateFNS,
  endOfDay,
  startOfDay,
  normalizeDate,
  differenceBetweenDates,
  isSameYear,
  formatDate,
  formatMonth,
  formatMonthYear,
  formatTechnicalMonthYear,
  formatTime,
  safeDateToString,
} from "@/utils/datetime";
import { clsx } from "clsx";
import { CLASS_CONSTANTS, ECourseColumns } from "@/utils/constants";
import type { PriceModelCollection } from "../Prices/PriceModelCollection";
import { CourseModelCollection } from "../Courses/CourseModelCollection";
import type { HandlerReturnType } from "./SlotValidator/types";
import type { FormFieldModelCollection } from "../FormFields/FormFieldModelCollection";
import type { NumberString } from "@/store/api/dto/options";
import type { TimespanModelDTO } from "@/store/api/dto/model.dto";
import type { PlaceModel } from "../Places/PlaceModel";

type DateSlot = {
  to: string;
  from: string;
  priority: number;
  slotInterval: number;
  minutesBefore: number;
  maxBookings: number;
};
export class CourseTimespanModel extends BookModel {
  declare presented: TimespanModelDTO["presented"];
  declare related: TimespanModelDTO["related"];
  declare from: string;
  declare to: string;

  declare show_free_places: boolean;

  static MAX_BOOKABLE_PLACES = 50;

  static cachedDates: Map<string, DateSlot[]> = new Map();
  static cachedSlots: Record<string, HandlerReturnType[]> = {};
  static cachedPriceStep: Record<string, { label: string; value: string }[]> =
    {};

  static dates: Array<string> = ["from", "to"];

  static burstCache() {
    CourseTimespanModel.cachedDates = new Map();
    CourseTimespanModel.cachedSlots = {};
    CourseTimespanModel.cachedPriceStep = {};
  }

  static getCollectionClass() {
    return CourseTimespanModelCollection;
  }

  get isAvailable() {
    return this.max_bookable_places_left >= 1;
  }

  get title() {
    return this.get("shortcut");
  }

  get maxQuantity() {
    return this.get("max_quantity") as number;
  }

  get shouldCollectParticipantInformation() {
    return (
      this.get("course_timespan_participation_type_name") !==
      "dont_collect_participants"
    );
  }

  get shouldTakeOnlyOneParticipant() {
    return (
      this.get("course_timespan_participation_type_name") ===
      "collect_one_participant"
    );
  }

  get max_bookable_places_left() {
    const places_left = this.placesLeft;
    return this.options.disableAvailabilityCheck
      ? Math.max(places_left, this.maxQuantity)
      : places_left;
  }

  get maxBookablePlacesLeftForReal() {
    const places_left = this.placesLeftForReal;
    return this.options.disableAvailabilityCheck
      ? Math.max(places_left, this.maxQuantity)
      : places_left;
  }

  get placesLeftForReal() {
    return this.get("places_left") as number;
  }

  getRecord(arg: { disableInfo?: boolean; fixedCourses?: string[] }) {
    const SymbolRecord = Symbol("record");
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const record: Record<ECourseColumns, unknown> = {
      get timespan() {
        return self.presented.titleReplaced;
      },
      get offer() {
        return self.id;
      },
      get categories() {
        return "";
      },
      get courses() {
        const courses = self.courses();
        if (arg.disableInfo) return SymbolRecord;
        return courses.get<string>("name").join(",");
      },
      get course() {
        const courses = self.courses();
        if (arg.disableInfo) return SymbolRecord;
        return courses.get<string>("name").join(",");
      },
      get places() {
        return self.place()?.get<string>("name") || "";
      },
      get place() {
        return self.place()?.get<string>("name") || "";
      },
      get trainer() {
        return self
          .teachers()
          .map((x) => x.id)
          .join(",");
      },
      get from() {
        return self.prepareAsRow({
          dataColumns: ["from"],
          placement: "course_table",
        })["from"];
      },
      get to() {
        return self.prepareAsRow({
          dataColumns: ["to"],
          placement: "course_table",
        })["to"];
      },
      get from_date_to_date() {
        return self.prepareAsRow({
          dataColumns: ["from_date_to_date"],
          placement: "course_table",
        })["from_date_to_date"];
      },
      get from_date() {
        return self.prepareAsRow({
          dataColumns: ["from_date"],
          placement: "course_table",
        })["from_date"];
      },
      get month() {
        return self.from && formatMonthYear(new Date(self.from));
      },
      get to_date() {
        return self.prepareAsRow({
          dataColumns: ["to_date"],
          placement: "course_table",
        })["to_date"];
      },
      get from_time() {
        return self.prepareAsRow({
          dataColumns: ["from_time"],
          placement: "course_table",
        })["from_time"];
      },
      get to_time() {
        return self.prepareAsRow({
          dataColumns: ["to_time"],
          placement: "course_table",
        })["to_time"];
      },
      get duration() {
        return self.prepareAsRow({
          dataColumns: ["duration"],
          placement: "course_table",
        })["duration"];
      },
      get price() {
        return self.customerPrices().get("id").join(",");
      },
      get info() {
        return self.prepareAsRow({
          dataColumns: ["info"],
          placement: "course_table",
        })["info"];
      },
      get appointments() {
        return self
          .appointments()
          .map((appointment) => appointment.basicInfo)
          .join(",");
      },
      get appointmentlist() {
        return self
          .appointments()
          .map((appointment) => appointment.basicInfo)
          .join(",");
      },
      get remaining() {
        return self.getPlacesLeftAsString(`course_table.n_places_left`, {
          key: "max_quantity_left_for_real",
        });
      },
      get infolink() {
        return self
          .courses()
          .all()
          .reduce(
            (acc: string[], curr) => {
              const courseDescription = curr.displayDescription;

              if (!courseDescription?.trim()) return acc;
              if (
                !arg.disableInfo &&
                arg.fixedCourses?.includes(courseDescription)
              )
                return acc;

              return [...acc, courseDescription?.trim() || ""];
            },
            [self.presented.body?.trim() || ""],
          )
          .filter(Boolean)
          .join("\n\n");
      },
      get booklink() {
        return self.id;
      },
    };

    return record;
  }

  get placesLeft() {
    const waitingListCount = this.nonBindingWaitlistIsBookedOut
      ? 0
      : (this.waitingListCount ?? 0);
    return this.placesLeftForReal > 0
      ? Math.max(this.placesLeftForReal, waitingListCount)
      : Math.max(this.placesLeftForReal + waitingListCount, 0);
  }

  get quantityLeftForReal() {
    return this.get("quantity_left") as number;
  }

  get quantityLeft() {
    const waitingListCount = this.nonBindingWaitlistIsBookedOut
      ? 0
      : (this.waitingListCount ?? 0);

    return Math.max(
      0,
      Math.min(
        this.maxQuantity,
        this.quantityLeftForReal > 0
          ? Math.max(this.quantityLeftForReal, waitingListCount)
          : this.quantityLeftForReal + waitingListCount,
      ),
    );
  }

  get max_quantity_left_for_real() {
    return (
      this.options.disableAvailabilityCheck
        ? this.max_bookable_places_left
        : this.quantityLeftForReal
    ) as number;
  }

  get max_quantity_left() {
    return (
      this.options.disableAvailabilityCheck
        ? this.max_bookable_places_left
        : this.quantityLeft
    ) as number;
  }

  get hasBillPersonalData() {
    // has_bill_personal_data: 1 means a separate bill recipient may be selected.
    return this.get("has_bill_personal_data") === 0;
  }

  get hideSeparateBillReceipient() {
    return this.get("hide_has_bill_personal_data") === 1;
  }

  get firstParticipantIsBillRecipient() {
    return this.hideSeparateBillReceipient && this.hasBillPersonalData;
  }

  get participantMayHaveBillRecipient() {
    return this.firstParticipantIsBillRecipient || !this.hasPrice;
  }

  get shouldHideBillRecipientStep() {
    return (
      !this.hasPrice ||
      (this.shouldCollectParticipantInformation &&
        this.firstParticipantIsBillRecipient)
    );
  }

  get isSchedulingTimespan() {
    return !!this.get("is_scheduling");
  }

  get reservedSchedulingSlots() {
    const data = (
      this.get("scheduling_reserved_times", [])! as ({
        from: string;
        to: string;
      } & Record<string, unknown>)[]
    ).map(({ from, to, ...props }) => {
      const toDate = normalizeDate(to);
      return {
        ...props,
        from: normalizeDate(from),
        to: toDate,
      };
    });

    return data;
  }

  get hasStepPricing() {
    return this.hasManyRelated("courseTimespansQuantitySteps");
  }

  get minimumQuantity() {
    return this.get("min_quantity_courses", 0);
  }

  get bookedOut() {
    return this.quantityLeftForReal <= 0;
  }

  get isNonBindingWaitingList() {
    return (
      !this.isSchedulingTimespan &&
      this.get("waitlist_type_name") === "non_binding_waitlist"
    );
  }

  get nonBindingWaitlistIsBookedOut() {
    return (
      this.isNonBindingWaitingList && this.options.nonBindingWaitlistIsBookedOut
    );
  }

  get waitingListCount() {
    if (this.nonBindingWaitlistIsBookedOut) return 0;
    return this.isSchedulingTimespan
      ? 0
      : (this.get("waitlist_left") as number);
  }

  get bookableWithWaitingList() {
    if (this.isSchedulingTimespan || !this.bookedOut) {
      return false;
    }

    return (this.get<number>("waitlist_left") ?? 0) > 0;
  }

  get hasLastBookingSlot() {
    return this.waitingListCount === 1 && this.bookedOut;
  }

  get info() {
    return (this.get("presented.titleReplaced") as string)?.trim();
  }

  get bookLink() {
    if (this.max_quantity_left <= 0 && !this.bookableWithWaitingList) return "";
    return this.presented.apiBookUrl || "";
  }

  get offer() {
    return this.id;
  }

  get from_date_to_date() {
    const fromDate = this.getFormattedDate("from");
    if (!fromDate) return null;

    const toDate = this.getFormattedDate("to");
    if (!toDate) return null;

    return `${fromDate} ${this.options.translate(`date.to_date_separator`)} ${toDate}`;
  }

  get hasPrice() {
    return !!this.customerPrices().size;
  }

  bookLinkWithDate(slot: BookingSlotInterface, addSlots = false) {
    const link = this.bookLink;
    if (!link) return link;

    const safeDate = safeDateToString(normalizeDate(slot.from), "date");
    if (!safeDate) return link;

    const url = new URL(link);
    const [hash, search] = url.hash.split("?");
    const searchParams = new URLSearchParams(search);

    searchParams.set("onDate", safeDate);

    if (addSlots) {
      searchParams.set("slotFrom", slot.from!);
      searchParams.set("slotTo", slot.to!);
    }

    url.hash = hash + "?" + searchParams.toString();
    return url.href;
  }

  getStepTitleAndDescription(prop: string) {
    return this.get(`presented.${prop}`) ?? {};
  }

  getMaxParticipantCount({
    selectedSlot,
    allowAllEvents,
  }: {
    allowAllEvents?: boolean;
    selectedSlot?: BookingSlotInterface | null;
  } = {}) {
    if (this.shouldTakeOnlyOneParticipant) {
      return 1;
    }

    if (!this.shouldCollectParticipantInformation) {
      return 1;
    }

    return allowAllEvents
      ? this.max_bookable_places_left
      : this.deriveQuantityLeft({ selectedSlot });
  }

  tags() {
    return this.hasMany("tags");
  }

  courses() {
    return this.hasMany<CourseModelCollection>("courses");
  }

  place() {
    return this.hasOne<PlaceModel>("place");
  }

  customerPrices() {
    return this.hasMany<PriceModelCollection>("customerPrices");
  }

  consents() {
    return this.hasMany<FormFieldModelCollection>("consents");
  }

  bookingMetaFields() {
    return this.hasMany<FormFieldModelCollection>("bookingMetaFormFields");
  }

  billRecipientFields(asBillRecipientCollection = false) {
    let billRecipientFieldsCollection = this.hasMany<FormFieldModelCollection>(
      "billReceiverFormFields",
    );

    if (asBillRecipientCollection) {
      billRecipientFieldsCollection =
        billRecipientFieldsCollection.mapCollection((x) =>
          x.toBillRecipientField(),
        );
    }

    return billRecipientFieldsCollection;
  }

  participantFormFields() {
    return this.hasMany<FormFieldModelCollection>("participantFormFields");
  }

  schedulingTimeRanges() {
    return this.hasMany<SchedulingTimeRangeModelCollection>(
      "schedulingTimeRanges",
    );
  }

  courseTimespansQuantitySteps() {
    return this.hasMany<CourseTimespansQuantityStepModelCollection>(
      "courseTimespansQuantitySteps",
    );
  }

  teachers() {
    try {
      return this.hasMany<BeeModelCollection<UserModel>>("teachers").all();
    } catch {
      return [];
    }
  }

  appointments() {
    try {
      return this.hasMany<BeeModelCollection<AppointmentModel>>(
        "computedAppointments",
      ).all();
    } catch {
      return [];
    }
  }

  getFromAsFilterName() {
    const date = this.get("from") as Date;
    return date ? formatTechnicalMonthYear(date) : null;
  }

  getFormattedDate(prop: "from" | "to") {
    const date = this.get(prop) as Date;
    if (!date) return null;

    return formatDate(date);
  }

  getFormattedTime(prop: "from" | "to") {
    const date = this.get(prop) as Date;
    if (!date) return null;

    return formatTime(date);
  }

  private prepareRecord({ maxPlacesThreshold = -1 } = {}) {
    const record: Record<string, unknown> & { id: string } = { id: this.id };

    record["courseNames"] = this.courses()
      .all()
      .map((c) => c.get("name"));
    record["tagNames"] = this.tags()
      .all()
      .map((c) => c.get("name"));

    record["fromRaw"] =
      this.getRaw("from.date") && parseISO(this.getRaw("from.date")).getTime();

    record["hasTrainers"] = this.teachers().length > 0;
    record["hasPrices"] = this.hasPrice;
    record["hasAppointments"] = !!this.get("from");

    const placesLeft = this.deriveQuantityLeft({
      key: "max_quantity_left_for_real",
    });

    record["hasFewPlacesLeft"] =
      !!maxPlacesThreshold && !!placesLeft && placesLeft <= maxPlacesThreshold;

    return record;
  }

  prepareAsRow(config: {
    maxPlacesThreshold?: number;
    dataColumns: string[];
    dryRun?: boolean;
    placement: string;
    disableInfo?: boolean;
    fixedCourses?: string[];
    fixedColumns?: string[];
  }) {
    const {
      dataColumns,
      dryRun,
      placement,
      disableInfo,
      fixedCourses = [],
      fixedColumns = [],
      maxPlacesThreshold = -1,
    } = config;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const timespan = this;
    const record = this.prepareRecord({
      maxPlacesThreshold,
    });

    const courseList = timespan.courses().all();

    return uniq(dataColumns.concat(fixedColumns ?? [])).reduce(
      (record, column) => {
        let value: unknown = "";

        switch (column) {
          case ECourseColumns.TIMESPAN: {
            value = dryRun
              ? ""
              : timespan.getAsTitle(fixedCourses, {
                  placement,
                  titleKey: "timespan_format",
                });
            break;
          }
          case ECourseColumns.OFFER: {
            value = dryRun
              ? ""
              : timespan.getAsTitle(fixedCourses, {
                  placement,
                  titleKey: "timespan_title",
                });
            break;
          }
          case ECourseColumns.COURSE: {
            value = courseList
              .filter(
                (course, index, self) =>
                  disableInfo ||
                  courseList.length === 1 ||
                  !fixedCourses.includes(course.name) ||
                  index === self.length - 1,
              )
              .map((course, remainingIndex) => {
                const indexInFcourses = fixedCourses.indexOf(course.name);

                const titleIndex =
                  indexInFcourses === -1
                    ? remainingIndex + fixedCourses.length
                    : indexInFcourses;

                return course.getAsTitle({
                  placement,
                  index: titleIndex,
                });
              })
              .join(" ");
            break;
          }
          case ECourseColumns.MONTH: {
            const fromDate = timespan.get<Date>("from");
            if (!fromDate) break;
            value = isSameYear(new Date(), fromDate)
              ? formatMonth(fromDate)
              : formatMonthYear(fromDate);
            break;
          }
          case ECourseColumns.CATEGORIES: {
            value = courseList.map((course, index) => ({
              key: course.name,
              label: course.getAsTitle({ placement, index }),
              description: course.displayDescription,
            }));
            break;
          }
          case ECourseColumns.PLACE: {
            const place = timespan.place();
            if (!place) break;
            value = {
              key: place.id,
              label: place.displayTitle,
              description: place.displayDescription,
            };
            break;
          }
          case ECourseColumns.TRAINER:
            value = timespan.teachers().map((teacher) => teacher.getTrainer());
            break;
          case ECourseColumns.APPOINTMENTS: {
            const from = timespan.getFormattedTime("from");
            if (!from) break;

            const to = timespan.getFormattedTime("to");
            if (!to) break;

            const appointments = timespan.appointments();
            const hasStartTimeBeforeEnd =
              from !== to &&
              appointments.every(
                (appointment) => from < (appointment.endTime ?? ""),
              );
            if (hasStartTimeBeforeEnd) {
              value = [
                this.options.translate(`${placement}.from_to_time`, {
                  fromTime: from,
                  toTime: to,
                }),
              ];
            } else {
              value = appointments.map((appointment) =>
                appointment.getFromToAuto(placement),
              );
            }
            break;
          }
          case ECourseColumns.APPOINTMENT_LIST: {
            const from = timespan.getFormattedTime("from");
            if (!from) break;

            value = timespan
              .appointments()
              .map((appointment) => appointment.getFromToAuto(placement));
            break;
          }
          case ECourseColumns.FROM: {
            const fromDate = timespan.getFormattedDate("from");
            record["fromRaw"] = parseISO(
              timespan.getRaw("from.date") || "0001-01-01",
            ).getTime();
            if (!fromDate) break;

            const fromTime = timespan.getFormattedTime("from");
            const toDate = timespan.getFormattedDate("to");
            const toTime =
              fromDate === toDate ? timespan.getFormattedTime("to") : "";
            const fromDateAuto = this.options.translate(
              `${placement}.from_date_auto`,
              {
                fromDate,
                fromTime,
                toTime:
                  toTime &&
                  `${this.options.translate(`date.to_date_separator`)} ${toTime}`,
              },
            );

            value = this.options.translate(`${placement}.from_date`, {
              fromDateAuto,
              fromDate,
              fromTime,
              toTime,
            });
            break;
          }
          case ECourseColumns.TO: {
            const fromDate = timespan.getFormattedDate("from")?.trim();
            const toDate = timespan.getFormattedDate("to")?.trim();
            record["toRaw"] = parseISO(
              timespan.getRaw("to.date") || "0001-01-01",
            ).getTime();
            if (!toDate) break;

            const toTime = timespan.getFormattedTime("to")?.trim();

            value =
              fromDate === toDate
                ? ""
                : this.options
                    .translate(`${placement}.to_date`, { toDate, toTime })
                    ?.trim() || "";

            break;
          }
          case ECourseColumns.FROM_DATE_TO_DATE: {
            value = timespan.get<string>(ECourseColumns.FROM_DATE_TO_DATE);
            break;
          }
          case ECourseColumns.FROM_DATE: {
            value = timespan.getFormattedDate("from");
            break;
          }
          case ECourseColumns.TO_DATE: {
            value = timespan.getFormattedDate("to");
            break;
          }
          case ECourseColumns.FROM_TIME: {
            value = timespan.getFormattedTime("from");
            break;
          }
          case ECourseColumns.TO_TIME: {
            value = timespan.getFormattedTime("to");
            break;
          }
          case ECourseColumns.DURATION: {
            const from = timespan.get<Date>("from");
            const to = timespan.get<Date>("to");
            if (!from || !to) break;

            value = differenceBetweenDates(to, from);
            break;
          }
          case ECourseColumns.INFO:
            value = this.info;
            break;
          case ECourseColumns.DESCRIPTION: {
            value = courseList
              .reduce((description: string[], course) => {
                const d = course.displayDescription.trim();
                if (!d || (!disableInfo && fixedCourses.includes(course.name)))
                  return description;

                return fixedCourses.includes(course.name) || !d
                  ? description
                  : [...description, d];
              }, [])
              .concat(timespan.get("presented.body", "").trim())
              .filter(Boolean)
              .join("");

            break;
          }
          case ECourseColumns.BOOKLINK:
            record["waitinglist"] = timespan.bookableWithWaitingList;
            value = timespan.bookLink;
            break;
          case ECourseColumns.PRICE: {
            const size = dryRun ? timespan.customerPrices().size : 0;
            if (dryRun && size) {
              value += "" + size;
            } else
              value =
                timespan.getCustomerPriceAsString("lowest", placement) ||
                this.options.translate(`${placement}.free`);
            break;
          }
          case ECourseColumns.REMAINING:
            value = timespan.getPlacesLeftAsString(
              `${placement}.n_places_left`,
              {
                key: "max_quantity_left_for_real",
              },
            );
            break;
        }
        record[column] = value;
        record["sortKey"] = (
          record["fromRaw"] +
          " " +
          record[ECourseColumns.COURSE]
        ).toLowerCase();
        return record;
      },
      record,
    );
  }

  prepareAsAppointment({
    placement = "course_table",
    enableBookingSlot = false,
  } = {}) {
    const record = this.prepareAsRow({
      dataColumns: ["booklink"],
      placement,
    });
    const title = this.getAsTitle([], {
      placement,
      titleKey: "timespan_title",
    });

    if (this.isSchedulingTimespan) {
      const appointmentsSource = enableBookingSlot
        ? this.getSchedulingSlots({ computeAheadOfTime: true }).filter(
            (x) => x.isAvailable,
          )
        : this.getSchedulingDates();

      const appointments = appointmentsSource.map((item) => ({
        ...record,
        hasAppointments: true,
        id: `${this.id}::(From::${item.from} To::${item.to})`,
        start: item.from,
        end: item.to,
        url: this.bookLinkWithDate(item, enableBookingSlot),
        isScheduling: true,
        title,
      }));

      return appointments;
    }

    return this.appointments().map((appointment) => ({
      ...record,
      id: appointment.id,
      start: appointment.get<Date>("from")!.toISOString(),
      end: appointment.get<Date>("to")!.toISOString(),
      url: this.bookLink,
      isScheduling: false,
      title,
    }));
  }

  getAvailablePrices({
    quantity,
    maxQuantity = null,
  }: {
    quantity?: NumberString;
    maxQuantity?: number | null;
  }) {
    if (this.hasStepPricing) {
      if (
        !Number(quantity) ||
        (maxQuantity !== null && maxQuantity < +quantity!)
      ) {
        return this.customerPrices().filter(
          () => false,
        ) as PriceModelCollection;
      }

      return this.customerPrices().availablePrices({ quantity });
    }

    return this.customerPrices();
  }

  get quantityTranslationPrefix() {
    if (this.get("voucher_type_name") === "value") {
      return "bookcart.voucher_quantity";
    }
    if (this.get("voucher_type_name") === "entitlement") {
      return this.get("voucher_initial_uses") === null
        ? "bookcart.unit_quantity"
        : "bookcart.card_quantity";
    }
    return null;
  }

  deriveStepsRange({
    bookingSlot,
  }: { bookingSlot?: BookingSlotInterface | null } = {}) {
    const key = `_${this.id}_${i18next.language}_${this.quantityTranslationPrefix}`;
    if (Object.hasOwn(CourseTimespanModel.cachedPriceStep, key))
      return CourseTimespanModel.cachedPriceStep[key];

    const totalQuantityLeft = this.options.disableAvailabilityCheck
      ? this.max_bookable_places_left
      : this.deriveQuantityLeft({ selectedSlot: bookingSlot });

    const steps = this.courseTimespansQuantitySteps()
      .generateStepRange({
        min_quantity: this.minimumQuantity,
        max_quantity: totalQuantityLeft,
      })
      .map((quantity) => {
        const priceId = this.getAvailablePrices({
          quantity: `${quantity}`,
          maxQuantity: totalQuantityLeft,
        }).first()?.id;

        return { priceId, quantity };
      });

    const hasDifferentPrices = !steps.every(
      (step) => step.priceId === steps[0]?.priceId,
    );
    const prefix = this.quantityTranslationPrefix;
    const translationKey = prefix
      ? `${prefix}.${hasDifferentPrices ? "option_with_price" : "option"}`
      : hasDifferentPrices
        ? "bookcart.step_participant"
        : "bookcart.step_participant_with_one_price";

    CourseTimespanModel.cachedPriceStep[key] = steps.map(
      ({ priceId, quantity }) => {
        const label = this.options.translate(translationKey, {
          quantity,
          count: quantity,
          amount: this.getCustomerPriceAsString(priceId as `${number}`),
          price_title: this.getPriceTitle(priceId, true),
        });

        return {
          value: String(quantity),
          label:
            quantity > this.maxBookablePlacesLeftForReal
              ? `${label} ${this.options.translate("price_summary.waitlist")}`
              : label,
        };
      },
    );

    return CourseTimespanModel.cachedPriceStep[key];
  }

  getBookerCountOptions({
    bookingSlot,
  }: {
    allowAllEvents?: boolean;
    bookingSlot?: BookingSlotInterface | null;
  } = {}) {
    if (this.hasStepPricing) {
      return this.deriveStepsRange({ bookingSlot });
    }

    const totalQuantityLeft = this.options.disableAvailabilityCheck
      ? this.max_bookable_places_left
      : this.deriveQuantityLeft({ selectedSlot: bookingSlot });
    const maxBoolablePlaces = this.maxBookablePlacesLeftForReal;
    return new Array(
      Math.min(+totalQuantityLeft, CourseTimespanModel.MAX_BOOKABLE_PLACES),
    )
      .fill(null)
      .map((_x, index) => {
        const participantLabelKey =
          index >= maxBoolablePlaces
            ? "bookcart.participant_waiting_list"
            : "bookcart.participant";
        const quantity = index + 1;
        const prefix = this.quantityTranslationPrefix;
        const label = prefix
          ? this.options.translate(`${prefix}.option`, {
              quantity,
              count: quantity,
            })
          : `${quantity} ${this.options.translate(participantLabelKey, { count: quantity })}`;
        return {
          value: String(quantity),
          label:
            prefix && index >= maxBoolablePlaces
              ? `${label} ${this.options.translate("price_summary.waitlist")}`
              : label,
        };
      });
  }

  deriveQuantityLeft({
    selectedSlot,
    key = "max_quantity_left",
  }: {
    selectedSlot?: BookingSlotInterface | null;
    key?: "max_quantity_left" | "max_quantity_left_for_real";
  } = {}) {
    if (!this.isSchedulingTimespan) {
      const maxQuantityLeft = this.get(key) as number;
      return Math.max(0, maxQuantityLeft);
    }

    const minMax = (count: number) =>
      Math.max(Math.min(this.maxQuantity, count), 0);

    if (!has(selectedSlot, "data.quantity_left")) {
      const maxQuantityPerSlot = this.schedulingTimeRanges()
        .all()
        .map((range) => (range as SchedulingTimeRangeModel).maxBookings);
      return minMax(Math.max(...maxQuantityPerSlot));
    }

    return minMax(get(selectedSlot, "data.quantity_left"));
  }

  getDateAsTitle(prop: "from" | "to") {
    const date = this.get(prop) as Date;
    if (!date) return null;

    return isSameYear(new Date(), date)
      ? formatMonth(date)
      : formatMonthYear(date);
  }

  getNormalizedFilterValues() {
    const months = this.getFromAsFilterName() || "-1";

    return {
      courses: this.courses().get<string>("name"),
      places: [this.place()?.get<string>("name")].filter((x) => !isNil(x)),
      months: [months],
    };
  }

  getCustomerPriceAsString(
    type:
      | null
      | `${number}`
      | "lowest"
      | "highest"
      | "last"
      | "first" = "lowest",
    placement = "bookcart",
  ) {
    const prices = this.customerPrices();

    if (prices.size === 0) return "";

    switch (type) {
      case "lowest":
        return prices.getLowestPrice(placement);
      case "highest":
        return prices.getHighestPrice(placement);
      case "last":
        return prices.getLastPrice(placement);
      case "first":
        return prices.getPrice(prices.first()?.id ?? null, placement);
      case null:
        return prices.getPrice(null, placement);
      default:
        return prices.getPrice(type, placement);
    }
  }

  getPlacesLeftAsString(
    translateToken?: string,
    deriveQuantityLeftArg?: {
      selectedSlot?: BookingSlotInterface;
      key?: "max_quantity_left" | "max_quantity_left_for_real";
    },
  ) {
    if (!this.options.isAdminIntegration && !this.get("show_free_places"))
      return "";

    const fullyBooked = !translateToken
      ? this.options.translate("bookcart.fully_booked", {
          max_quantity: this.maxBookablePlacesLeftForReal,
          booking_count: this.get("booking_count") as number,
        })
      : "";

    if (!translateToken) translateToken = "bookcart.n_places_left";

    const realQuantityLeft = this.deriveQuantityLeft({
      ...deriveQuantityLeftArg,
      key: "max_quantity_left_for_real",
    }) as number;

    return realQuantityLeft <= 0
      ? fullyBooked
      : this.options.translate(translateToken, {
          count: this.maxBookablePlacesLeftForReal,
          max_quantity: this.maxBookablePlacesLeftForReal,
          booking_count: this.get("booking_count") as number,
        });
  }

  getDateString() {
    const from = this.getFormattedDate("from");
    const to = from && this.getFormattedDate("to");

    if (!from) return "";
    if (from === to) return from;

    return `${from} - ${to}`;
  }

  getPriceTitle(priceId: `${number}`, showSinglePriceTitle = false) {
    const c = this.customerPrices();
    if (!showSinglePriceTitle && c.size === 1) {
      return "";
    }

    if (!showSinglePriceTitle && !priceId) {
      if (c.size === 1) return "";
    }

    const price = c.findById(priceId);

    return price?.get("presented.customerTitle", price.get("title", "")) ?? "";
  }

  getAsTitle(
    excludedCourseNames: string[] = [],
    {
      placement = "bookcart",
      titleKey = "timespan_format",
      hidePlacesLeft = false,
      hidePrice = false,
      hideDateTime = false,
    } = {},
    extraObject: {
      priceId?: `${number}` | null;
      selectedSlot?: BookingSlotInterface | null;
      showSinglePriceTitle?: boolean;
      isBookingWaitlist?: boolean;
    } | null = null,
  ) {
    // const placesLeft = this.getPlacesLeftAsString()
    // const price = this.getCustomerPriceAsString();
    // const dateString = this.getDateString();
    if (this.options.isAdminIntegration)
      return this.options.translate(`bookcart.timespan_format`, {
        course1: "",
        course2: "",
        course3: "",
        course4: "",
        course5: "",
        course6: "",
        course7: "",
        course8: "",
        course9: "",
        course10: "",
        fromDate: "",
        toDate: "",
        fromTime: "",
        toTime: "",
        fromToAuto: "",
        place: "",
        totalPrice: "",
        price: "",
        highestPrice: "",
        lowestPrice: "",
        lastPrice: "",
        waitlist: "",
        title: this.get("shortcut"),
        placesLeft: this.getPlacesLeftAsString(),
        bookingSlot: "",
        skipInterpolation: true,
      });

    const priceId = extraObject?.priceId || null;
    const selectedSlot =
      extraObject?.selectedSlot || (null as BookingSlotInterface | null);

    const showSinglePriceTitle = extraObject?.showSinglePriceTitle || false;
    const isWaitlistBooking = extraObject?.isBookingWaitlist || false;
    const title = this.get("presented.titleReplaced", "");
    const subscriptionTerms = this.get("presented.subscriptionTerms", "");

    const wrapInSpan = (key: string, value?: string | null) => {
      const className = clsx(key, {
        [CLASS_CONSTANTS.TIMESPAN.TITLE_PRICE]: key
          .toLowerCase()
          .includes("price"),
        [CLASS_CONSTANTS.TIMESPAN.LAST_COURSE]:
          `course${courseData.length}` === key,
      });

      return value?.trim() ? `<span class="${className}">${value}</span>` : "";
    };

    const excludedCourseIds = this.courses()
      .whereIn("name", excludedCourseNames)
      .get<string>("id");

    const courseData = this.courses()
      .filter((m) => !excludedCourseIds.includes(m.id))
      .all()
      .map((m) => m.displayTitle.trim(), [] as string[])
      .filter(Boolean);

    const courses = courseData.reduce(
      (courseObject: Record<string, string>, value, i) => {
        const key = `course${i + 1}`;

        courseObject[key] = wrapInSpan(key, value);
        return courseObject;
      },
      {
        course1: "",
        course2: "",
        course3: "",
        course4: "",
        course5: "",
        course6: "",
        course7: "",
        course8: "",
        course9: "",
        course10: "",
      },
    );

    const toDateSeparator = this.options.translate(`date.to_date_separator`);

    const fromDate = this.getFormattedDate("from");
    const fromTime = this.getFormattedTime("from");
    const toDate = this.getFormattedDate("to");
    const toTime = this.getFormattedTime("to");

    const prependToDateSeparator = (str?: string | null) => {
      if (!str?.trim()) return "";
      return `${toDateSeparator} ${str}`;
    };

    const fromToAuto = (
      startDate: string | null,
      startTime: string | null,
      endDate: string | null,
      endTime: string | null,
    ) => {
      const isSameDay = startDate === endDate;
      const isSameTime = startTime === endTime;

      return this.options.translate(`${placement}.from_to_auto`, {
        fromDate: startDate,
        fromTime: startTime,
        toDate: isSameDay ? "" : prependToDateSeparator(endDate),
        toTime: !isSameDay
          ? endTime
          : isSameTime
            ? ""
            : prependToDateSeparator(endTime),
      });
    };

    const titleProps = {
      fromDate: hideDateTime ? "" : () => wrapInSpan("fromDate", fromDate),
      toDate: hideDateTime ? "" : () => wrapInSpan("toDate", toDate),
      fromTime: hideDateTime ? "" : () => wrapInSpan("fromTime", fromTime),
      toTime: hideDateTime ? "" : () => wrapInSpan("toTime", toTime),
      fromToAuto: hideDateTime
        ? ""
        : () =>
            wrapInSpan(
              "fromToAuto",
              fromToAuto(fromDate, fromTime, toDate, toTime),
            ),
      title: () =>
        [
          wrapInSpan("title", title),
          wrapInSpan("subscriptionTerms", subscriptionTerms),
        ]
          .filter(Boolean)
          .join(" "),
      waitlist: !isWaitlistBooking
        ? ""
        : () =>
            wrapInSpan(
              "waitlist",
              this.options.translate(`${placement}.waitlist`, {
                defaultValue: "",
              }),
            ),
      place: () => wrapInSpan("place", this.place()?.displayTitle),
      placesLeft: hidePlacesLeft
        ? ""
        : () => wrapInSpan("placesLeft", this.getPlacesLeftAsString()),
      priceTitle: hidePrice
        ? ""
        : () =>
            wrapInSpan(
              "priceTitle",
              !priceId ? "" : this.getPriceTitle(priceId, showSinglePriceTitle),
            ),
      totalPrice: hidePrice
        ? ""
        : () =>
            wrapInSpan("totalPrice", this.getCustomerPriceAsString(priceId)),
      price: hidePrice
        ? ""
        : () => wrapInSpan("price", this.getCustomerPriceAsString(priceId)),
      highestPrice: hidePrice
        ? ""
        : () =>
            wrapInSpan(
              "highestPrice",
              this.getCustomerPriceAsString("highest"),
            ),
      lowestPrice: hidePrice
        ? ""
        : () =>
            wrapInSpan("lowestPrice", this.getCustomerPriceAsString("lowest")),
      lastPrice: hidePrice
        ? ""
        : () => wrapInSpan("lastPrice", this.getCustomerPriceAsString("last")),

      bookingSlot:
        !selectedSlot?.from || !selectedSlot?.to
          ? ""
          : () =>
              wrapInSpan(
                "bookingSlot",
                fromToAuto(
                  formatDate(normalizeDate(selectedSlot!.from!)),
                  formatTime(normalizeDate(selectedSlot!.from!)),
                  formatDate(normalizeDate(selectedSlot!.to!)),
                  formatTime(normalizeDate(selectedSlot!.to!)),
                ),
              ),
      ...courses,
    };

    return i18next
      .t(`${placement}.${titleKey}`, "", {
        ...titleProps,
        skipInterpolation: true,
      })
      .replace(/\s+/g, " ");
  }

  getSchedulingDates(configuration: { bookers_count?: number | string } = {}) {
    if (!this.isSchedulingTimespan) return [];

    const { bookers_count = 1 } = configuration;
    const key = `_${this.id}._${bookers_count}._${this.options.computeTimeslotsAheadOfTime}`;

    if (CourseTimespanModel.cachedDates.has(key)) {
      return CourseTimespanModel.cachedDates.get(key)!;
    }

    const trCollection = this.schedulingTimeRanges(),
      maxDaysInFuture = this.get("schedule_max_days_in_future") as number;

    const dates = trCollection.generateIncludedRangesForDays(maxDaysInFuture);

    if (!this.options.computeTimeslotsAheadOfTime) {
      CourseTimespanModel.cachedDates.set(key, dates);
      return dates;
    }

    const availableSlots = Array.from(
      new Set(
        this.getSchedulingSlots({ ...configuration, computeAheadOfTime: true })
          .filter(({ isAvailable }) => isAvailable)
          .map(({ from }) => startOfDay(from!).toISOString()),
      ),
    );

    CourseTimespanModel.cachedDates.set(
      key,
      dates.filter(({ from }) =>
        availableSlots.includes(startOfDay(from).toISOString()),
      ),
    );

    return CourseTimespanModel.cachedDates.get(key)!;
  }

  normalizeParticipantData(state: object, prefix: string, index?: number) {
    this.participantFormFields()
      .all()
      .map((field) => {
        merge(state, field.normalizeFieldValue(state, prefix, index));
      });
  }

  normalizeBillRecipientData(state: object, prefix: string, index?: number) {
    this.billRecipientFields()
      .all()
      .forEach((field) => {
        merge(state, field.normalizeFieldValue(state, prefix, index));
      });
  }

  getSchedulingSlots(configuration = {}) {
    const date = normalizeDate(get(configuration, "onDate", null));
    const shouldCalculateAheadOfTime = !!get(
      configuration,
      "computeAheadOfTime",
      false,
    );
    const startOfDate = shouldCalculateAheadOfTime
      ? null
      : date && startOfDay(new Date(date));
    const bookersCount = get(configuration, "bookers_count", -1);
    const key = `_${this.id}._${bookersCount}._${shouldCalculateAheadOfTime}._${startOfDate}`;
    if (has(CourseTimespanModel.cachedSlots, key))
      return get(CourseTimespanModel.cachedSlots, key);

    let fail = false;

    if (!this.isSchedulingTimespan) {
      fail = true;
    }

    if (!shouldCalculateAheadOfTime && !startOfDate) {
      fail = true;
    }

    if (fail) {
      set(CourseTimespanModel.cachedSlots, key, []);
      return get(CourseTimespanModel.cachedSlots, key);
    }

    const trCollection = this.schedulingTimeRanges(),
      daysInFuture = this.get("schedule_max_days_in_future") as number;

    const excluded = trCollection.generateExcludedRangesForDays(
      daysInFuture,
      startOfDate,
    );
    const included = trCollection.generateIncludedRangesForDays(
      daysInFuture,
      startOfDate,
    );

    const availableSlotDates = trCollection.generateAvailableSlotDates(
      daysInFuture,
      startOfDate,
    );

    const now = setDateFNS(new Date(), { milliseconds: 0, seconds: 0 });
    const firstIncludedRangeFromNow = included.find((range) => {
      return addMinutes(now, range.slotInterval) <= normalizeDate(range.to);
    });

    const from = firstIncludedRangeFromNow
      ? setMilliseconds(
          addMinutes(new Date(), firstIncludedRangeFromNow?.minutesBefore),
          0,
        )
      : endOfDay(now);

    const availabilityManager = getSlotHandler({
      bookersCount: +bookersCount,
      availableSlots: included,
      excludedSlots: excluded,
      bookedEvents: this.reservedSchedulingSlots
        .filter((slot) => from < slot.from)
        .map((reservedSlots) => ({
          ...reservedSlots,
          from: reservedSlots.from.toISOString(),
          to: reservedSlots.to.toISOString(),
        })),
      from,
      to: normalizeDate(last(included)?.to),
    });

    const slotStatuses = availableSlotDates.map(
      ({ slotStart, slotEnd, maxBookings, priority }) => {
        const quantityLeft = this.options.disableAvailabilityCheck
          ? this.get<number>("max_bookable_places_left")!
          : maxBookings;
        return availabilityManager.getStatus(
          slotStart,
          slotEnd,
          quantityLeft,
          priority,
        );
      },
    );

    set(CourseTimespanModel.cachedSlots, key, slotStatuses);
    return get(CourseTimespanModel.cachedSlots, key);
  }

  #participantDefaultData: null | Record<string, unknown> = null;
  getDefaultParticipantData() {
    if (this.#participantDefaultData) return this.#participantDefaultData;

    this.#participantDefaultData = this.participantFormFields()
      .all()
      .reduce(
        (acc, field) => ({
          ...acc,
          [field.getItemName()]: field.defaultValue ?? undefined,
        }),
        {},
      );

    return this.#participantDefaultData;
  }

  #billRecipientDefaultData: null | Record<string, unknown> = null;
  getDefaultBillRecipientData() {
    if (this.#billRecipientDefaultData) return this.#billRecipientDefaultData;
    this.#billRecipientDefaultData = this.billRecipientFields()
      .all()
      .reduce(
        (acc, field) => ({
          ...acc,
          [field.getItemName()]: field.defaultValue ?? undefined,
        }),
        {},
      );
    return this.#billRecipientDefaultData;
  }
}
